import { useState, useEffect } from 'react';

import { useLeague } from '../hooks/useLeague';
import { getLeagues, type League } from '../services/leagueService';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import ErrorDisplay from '@/components/ui/ErrorDisplay';
import { LeagueHeader } from '../components/LeagueHeader';
import { LeaguePodium } from '../components/LeaguePodium';
import { LeaderboardList } from '../components/LeaderboardList';


const LeaguePage = () => {
  const { leaderboard, preview, loading, error, refetch } = useLeague();
  const [leagues, setLeagues] = useState<League[]>([]);

  // Fetch leagues for the header
  useEffect(() => {
    (async () => {
      try {
        const ls = await getLeagues();
        setLeagues(ls.sort((a, b) => a.rank_level - b.rank_level));
      } catch (e) {
        // ignore
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-primary-500 flex items-center justify-center">
        <LoadingSpinner className="text-white" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-primary-500 flex items-center justify-center p-4">
        <ErrorDisplay message={error} onRetry={refetch} />
      </div>
    );
  }

  if (!leaderboard) {
    if (preview?.state === 'needs_activity') {
      const targetLeague = leagues.find(league => league.league_id === preview.target_league_id);
      return (
        <div className="min-h-screen bg-primary-500 flex flex-col items-center pt-safe-top" dir="rtl">
          <div className="w-full max-w-md mx-auto mt-2">
            <LeagueHeader leagues={leagues} currentRankLevel={targetLeague?.rank_level ?? 1} />
            <div className="mx-6 mt-8 rounded-3xl bg-white/10 border border-white/15 p-6 text-center text-white shadow-lg">
              <p className="font-persian mt-3 leading-8">{preview.message}</p>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-primary-500 flex items-center justify-center text-white font-persian">
        اطلاعات لیگ در دسترس نیست
      </div>
    );
  }

  // Split participants into Top 3 and Rest
  // Ensure we sort by rank just in case
  const sortedParticipants = [...leaderboard.participants].sort((a, b) => a.rank - b.rank);
  const topThree = sortedParticipants.filter(p => p.rank <= 3);
  const rest = sortedParticipants.filter(p => p.rank > 3);

  return (
    <div className="min-h-screen bg-primary-500 dark:bg-primary-500 flex flex-col items-center relative overflow-hidden pt-safe-top" dir="rtl">
      {/* Background Decoration (Subtle Gradient) */}


      {/* Centered Content Container */}
      <div className="w-full max-w-md mx-auto flex flex-col flex-1 mt-2">
        {/* Header with League Tiers */}
        <LeagueHeader
          leagues={leagues}
          currentRankLevel={leaderboard.rank_level}
        />

        {/* Timer - Centered between Icons and Podium */}
        <div className="flex justify-center mt-4 mb-6 relative z-20">
          <div className="bg-white/10 backdrop-blur-sm rounded-full px-4 py-1.5 text-white text-xs font-medium font-persian border border-white/5 shadow-sm">
            {Math.floor(leaderboard.remaining_time_hours / 24) > 0 ? (
              <>
                {String(Math.floor(leaderboard.remaining_time_hours / 24)).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)])} روز و {' '}
                {String(leaderboard.remaining_time_hours % 24).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)])} ساعت
              </>
            ) : (
              <>
                {String(leaderboard.remaining_time_hours).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)])} ساعت
              </>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col mt-0">
          {/* Top 3 Podium */}
          <LeaguePodium
            topThree={topThree}
          />

          {/* List Sheet */}
          <div className="flex-1">
            <LeaderboardList
              participants={rest}
              currentUserId={leaderboard.participants.find(p => p.is_current_user)?.user_id}
              promotionRank={leaderboard.promotion_rank}
              demotionRank={leaderboard.demotion_rank}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaguePage;
